import { Injectable } from '@angular/core';
import { MenuList } from '@shared/models/menu-list';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SiteSettingsService } from '../../shared/services/site-settings.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(
    private siteSettingsService: SiteSettingsService,
    private customisableVariablesService: CustomisableVariablesService
  ) {}

  public getNavigationLinks(
    type: 'main' | 'secondary' | 'burger-menu' | 'content-outside' | 'content-outside-footer' | 'footer' = 'main'
  ): Observable<MenuList[]> {
    return combineLatest([
      this.siteSettingsService.fetchSiteSettings(),
      this.customisableVariablesService.variables,
    ]).pipe(
      filter(([siteSettings, variables]) => !!siteSettings.id && !!Object.keys(variables).length),
      take(1),
      map(([siteSettings, variables]) => {
        const switches = siteSettings.switches;
        const brandPath = siteSettings.fe_configuration.path;

        switch (type) {
          case 'main':
            return [
              {
                name: 'Travel',
                id: 'nav_travel',
                ariaLabel: 'travel menu',
                translate: 'GlobalNav:NavTravel',
                customName: variables.navigation_travel_link_text,
                routerLink: '/travel',
                isVisible:
                  switches.travel_module &&
                  (switches.travel_hotels_module ||
                    switches.travel_flights_module ||
                    switches.travel_car_hire_module ||
                    switches.travel_activities_module ||
                    siteSettings.travel_services.length > 0),
              },
              {
                name: 'Dining',
                id: 'nav_dining',
                ariaLabel: 'dining menu',
                translate: 'GlobalNav:NavDining',
                customName: variables.navigation_dining_link_text,
                routerLink: '/dining',
                isVisible: switches.dining_module,
              },
              {
                name: 'Entertainment',
                id: 'nav_entertainment',
                ariaLabel: 'entertainment menu',
                translate: 'GlobalNav:NavEntertainment',
                customName: variables.navigation_entertainment_link_text,
                routerLink: '/entertainment',
                isVisible: switches.entertainment_module,
              },
              {
                name: 'Events',
                id: 'nav_events',
                ariaLabel: 'events menu',
                translate: 'GlobalNav:NavEvents',
                customName: variables.navigation_events_link_text,
                routerLink: '/member-events',
                isVisible: switches.member_events_module,
              },
              {
                name: 'Offers',
                id: 'nav_offers',
                ariaLabel: 'offers menu',
                translate: 'GlobalNav:NavOffers',
                customName: variables.navigation_offers_link_text,
                routerLink: '/member-benefits',
                isVisible: switches.member_benefits_module,
              },
              {
                name: 'Partners',
                id: 'nav_partners',
                ariaLabel: 'partners menu',
                translate: 'GlobalNav:Partners',
                customName: variables.navigation_partners_link_text,
                routerLink: '/partners',
                isVisible: switches.partners_module,
              },
              {
                name: 'Inspiration',
                id: 'nav_inspiration',
                ariaLabel: 'inspiration menu',
                translate: 'GlobalNav:NavInspiration',
                customName: variables.navigation_inspiration_link_text,
                routerLink: 'inspiration',
                isVisible: switches.inspiration_module,
              },
              {
                name: 'Benefits',
                id: 'nav_benefits',
                ariaLabel: 'benefits menu',
                translate: 'GlobalNav:NavBenefits',
                customName: variables.navigation_benefits_link_text,
                routerLink: '/benefits',
                isVisible: switches.enable_centurion_benefits, // Temp until I can convince BE to change it
                hasSubNavigation: true,
              },
              {
                name: 'RequestManagement',
                id: 'nav_request_management',
                ariaLabel: 'request management',
                translate: 'RequestManagement:ModuleTitle',
                customName: '',
                title: 'Request Management',
                routerLink: '/request-management',
                isVisible: switches.requests_module && switches.request_management_feature,
              },
            ];

          case 'secondary':
            return [
              {
                isVisible: true,
                routerLink: '/product/accessibility',
                translate: 'GlobalNav:NavAccessibility',
              },
              {
                isVisible: true,
                routerLink: '/product/privacy',
                translate: 'GlobalNav:NavPrivacy',
              },
              {
                isVisible: true,
                routerLink: '/product/terms',
                translate: 'GlobalNav:NavTerms',
              },
              {
                isVisible: true,
                routerLink: '/product/faq',
                translate: 'GlobalNav:NavFaq',
              },
              {
                isVisible: switches.access_to_HTR,
                routerLink: '/product/contact',
                translate: 'GlobalNav:NavContact',
              },
              {
                isVisible: switches.member_activity_covid_support_points || switches.travel_covid_support_points,
                routerLink: '/product/covid',
                customName: variables.sitata_link_covid,
              },
            ];
          case 'footer':
            return [
              {
                isVisible: true,
                routerLink: 'product/accessibility',
                translate: 'FooterComponent:FooterAccessibility',
              },
              {
                isVisible: true,
                routerLink: 'product/privacy',
                translate: 'FooterComponent:FooterPrivacy',
              },
              {
                isVisible: true,
                routerLink: 'product/terms',
                translate: 'FooterComponent:FooterTerms',
              },
              {
                isVisible: true,
                routerLink: 'product/faq',
                translate: 'FooterComponent:FooterFaq',
              },
              {
                isVisible: switches.access_to_HTR,
                routerLink: 'product/contact',
                translate: 'FooterComponent:FooterContact',
              },
              {
                isVisible: switches.member_activity_covid_support_points || switches.travel_covid_support_points,
                routerLink: 'product/covid',
                customName: variables.sitata_link_covid,
              },
            ];
          case 'burger-menu':
            return [
              {
                isVisible: true,
                routerLink: variables.MTLS_PreLogin_Featured_Bottom3_Link_1,
                customName: variables.MTLS_PreLogin_Featured_Bottom3_Title_1,
              },
              {
                isVisible: true,
                routerLink: variables.MTLS_PreLogin_Featured_Bottom3_Link_2,
                customName: variables.MTLS_PreLogin_Featured_Bottom3_Title_2,
              },
              {
                isVisible: true,
                routerLink: variables.MTLS_PreLogin_Featured_Bottom3_Link_3,
                customName: variables.MTLS_PreLogin_Featured_Bottom3_Title_3,
              },
            ];
          case 'content-outside':
            return [
              {
                isVisible: switches.pre_login_home_page,
                routerLink: `product/${brandPath}/homepage`,
                translate: 'PreLoginNavigation:Home',
                eventName: 'Prelogin - Navigation Home Selected',
              },
              {
                isVisible: switches.pre_login_benefits_page,
                routerLink: 'product/benefits',
                translate: 'PreLoginNavigation:Benefits',
                eventName: 'Prelogin - Navigation Benefits Selected',
              },
              {
                isVisible: switches.pre_login_inspiration_page,
                routerLink: 'product/inspiration',
                translate: 'PreLoginNavigation:Inspiration',
                eventName: 'Prelogin - Navigation Inspiration Selected',
              },
            ];
          case 'content-outside-footer':
            return [
              {
                isVisible: switches.pre_login_home_page,
                routerLink: '/',
                translate: 'PreLoginNavigation:Home',
              },
              {
                isVisible: switches.pre_login_home_page && switches.pre_login_benefits_page,
                routerLink: '/product/benefits',
                translate: 'PreLoginNavigation:Benefits',
              },
              {
                isVisible: switches.pre_login_home_page && switches.pre_login_inspiration_page,
                routerLink: '/product/inspiration',
                translate: 'PreLoginNavigation:Inspiration',
              },
            ];
          default:
            return [];
        }
      })
    );
  }
}
